import React, { useState, useEffect } from 'react';

// Preview-only component: floating dropdown to switch between test users.
// Only renders when REACT_APP_PREVIEW_MODE is 'true'.

const PreviewUserSwitcher: React.FC = () => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isOpen, setIsOpen] = useState(false);

  const users: Array<{ uid: string; name: string; email: string }> =
    (window as any).__PREVIEW_USERS || [];
  const getIndex: () => number = (window as any).__getPreviewUserIndex || (() => 0);
  const switchUser: (idx: number) => void = (window as any).__switchPreviewUser || ((_idx: number) => { /* preview noop */ }); // eslint-disable-line @typescript-eslint/no-empty-function

  useEffect(() => {
    setSelectedIndex(getIndex());
  }, []);

  if (process.env.REACT_APP_PREVIEW_MODE !== 'true' || users.length === 0) {
    return null;
  }

  const handleSwitch = (index: number) => {
    setSelectedIndex(index);
    switchUser(index);
    setIsOpen(false);
    // Reload to re-initialize the app with the new user
    window.location.reload();
  };

  const selectedUser = users[selectedIndex];

  return (
    <div style={styles.container}>
      <div style={styles.banner}>PREVIEW MODE</div>
      <button
        style={styles.button}
        onClick={() => setIsOpen(!isOpen)}
      >
        {selectedUser?.name || 'Select User'} ▾
      </button>
      {isOpen && (
        <div style={styles.dropdown}>
          {users.map((user, index) => (
            <button
              key={user.uid}
              style={{
                ...styles.option,
                ...(index === selectedIndex ? styles.optionSelected : {}),
              }}
              onClick={() => handleSwitch(index)}
            >
              <strong>{user.name}</strong>
              <br />
              <span style={styles.email}>{user.email}</span>
              <span style={styles.role}>
                {user.uid.startsWith('author') ? 'Author' : 'Reader'}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    position: 'fixed',
    bottom: '16px',
    right: '16px',
    zIndex: 99999,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontSize: '13px',
  },
  banner: {
    backgroundColor: '#ff6b35',
    color: 'white',
    padding: '4px 12px',
    borderRadius: '4px 4px 0 0',
    fontWeight: 'bold',
    fontSize: '11px',
    letterSpacing: '1px',
    textAlign: 'center',
  },
  button: {
    backgroundColor: '#1a1a2e',
    color: 'white',
    border: 'none',
    padding: '10px 16px',
    borderRadius: '0 0 4px 4px',
    cursor: 'pointer',
    width: '100%',
    textAlign: 'left',
    fontSize: '13px',
  },
  dropdown: {
    position: 'absolute',
    bottom: '100%',
    right: 0,
    backgroundColor: 'var(--br-surface)',
    border: '1px solid var(--br-border)',
    borderRadius: '4px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    minWidth: '240px',
    marginBottom: '4px',
    maxHeight: '300px',
    overflowY: 'auto',
  },
  option: {
    display: 'block',
    width: '100%',
    padding: '10px 16px',
    border: 'none',
    borderBottom: '1px solid var(--br-border)',
    backgroundColor: 'var(--br-surface)',
    color: 'var(--br-text)',
    cursor: 'pointer',
    textAlign: 'left',
    fontSize: '13px',
  },
  optionSelected: {
    backgroundColor: 'var(--br-raised-surface)',
  },
  email: {
    color: 'var(--br-muted)',
    fontSize: '11px',
  },
  role: {
    float: 'right',
    color: 'var(--br-muted)',
    fontSize: '11px',
    marginTop: '2px',
  },
};

export default PreviewUserSwitcher;
